// LoginFormSection.js — El acceso como una sección más de la página, no como
// un popup: la tarjeta con correo y contraseña a un lado y la imagen de campaña
// al otro (así es la pantalla de HepCampus).
//
// El armazón (fondo, columnas, barra de campaña, tarjeta, imágenes) lo pone
// PanelAcceso, compartido con la sección de registro. Acá vive el formulario.
// Todo el contenido sale de la config de la sección, que se edita desde el
// Landing Builder: la sección no sabe nada de HepCampus.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authService, API_BASE_URL } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import PanelAcceso, { resolverImagen } from './PanelAcceso';
import VerContrasena from '../../VerContrasena';

const Campo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: ${p => p.$padding || '10px 4px'};
  margin-bottom: ${p => p.$margin || '18px'};
  /* Estilo "línea": solo el filete de abajo, como en el diseño. Con "caja" el
     campo se dibuja como un input clásico con borde completo. */
  ${p => p.$caja
    ? `border: 1px solid ${p.$borde || '#d8dde6'}; border-radius: ${p.$radio || '10px'}; padding: ${p.$padding || '10px 14px'};`
    : `border-bottom: 1px solid ${p.$borde || '#d8dde6'};`}
  transition: border-color 0.2s;
  &:focus-within {
    ${p => p.$caja
      ? `border-color: ${p.$bordeFocus || p.$acento || '#21315c'};`
      : `border-bottom-color: ${p.$bordeFocus || p.$acento || '#21315c'};`}
  }
`;

const Icono = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${p => p.$size || '30px'};
  min-width: ${p => p.$size || '30px'};
  color: ${p => p.$color || '#9aa3b2'};
  font-size: ${p => p.$size || '30px'};
  img { width: 100%; height: auto; display: block; }
  svg { width: 100%; height: auto; display: block; }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: inherit;
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#333333'};
  &::placeholder { color: ${p => p.$phColor || '#9aa3b2'}; }
`;

const Etiqueta = styled.label`
  display: block;
  font-size: ${p => p.$size || '14px'};
  font-weight: 600;
  color: ${p => p.$color || '#475569'};
  margin-bottom: 6px;
`;

const Recordar = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: ${p => p.$margin || '22px 0 0'};
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#5b6472'};
  cursor: pointer;
  input {
    width: ${p => p.$box || '18px'};
    height: ${p => p.$box || '18px'};
    accent-color: ${p => p.$acento || '#21315c'};
    cursor: pointer;
  }
`;

const Acciones = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$align || 'center'};
  margin-top: ${p => p.$margin || '26px'};
`;

const Boton = styled.button`
  background: ${p => p.$bg || 'transparent'};
  color: ${p => p.$color || '#21315c'};
  border: ${p => p.$borde || '2px solid #21315c'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '10px 42px'};
  font-family: inherit;
  font-size: ${p => p.$size || '19px'};
  font-weight: ${p => p.$weight || 700};
  ${p => p.$ancho ? `width: ${p.$ancho};` : ''}
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.82; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Enlace = styled.a`
  display: inline-block;
  margin-top: ${p => p.$margin || '18px'};
  color: ${p => p.$color || '#21315c'};
  font-size: ${p => p.$size || '17px'};
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const Cambio = styled.p`
  margin: 14px 0 0;
  font-size: ${p => p.$size || '16px'};
  color: ${p => p.$color || '#5b6472'};
  a { color: ${p => p.$linkColor || '#21315c'}; font-weight: 600; text-decoration: none; cursor: pointer; }
  a:hover { text-decoration: underline; }
`;

const Error = styled.div`
  margin-top: 14px;
  color: ${p => p.$color || '#b91c1c'};
  background: ${p => p.$bg || 'rgba(185,28,28,0.08)'};
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 15px;
  text-align: center;
`;

// Iconos por defecto: el sobre y la llave del diseño. El admin puede sustituir
// cualquiera por un emoji o por una imagen subida.
const IconoSobre = () => (
  <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="1" y="1" width="30" height="22" rx="2" />
    <path d="M1.5 2.5 16 14 30.5 2.5" />
  </svg>
);

const IconoLlave = () => (
  <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="8" cy="12" r="5.5" />
    <path d="M13.5 12H29M23 12v4.5M29 12v5.5" />
  </svg>
);

// Un icono puede ser una imagen subida, un emoji/carácter, o el SVG por defecto.
const renderIcono = (valor, PorDefecto) => {
  if (!valor) return <PorDefecto />;
  if (valor.startsWith('http') || valor.startsWith('uploads/')) {
    return <img src={resolverImagen(valor)} alt="" />;
  }
  return <span>{valor}</span>;
};

const LoginFormSection = ({ config, proyecto, tl }) => {
  const c = config || {};
  const t = (v, def = '') => (tl ? tl(v, def) : (v || def));
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recordar, setRecordar] = useState(true);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  // Variantes de acceso del proyecto (entrar con documento, o sin contraseña).
  // Vienen del mismo endpoint público que usa el modal.
  const [registroConfig, setRegistroConfig] = useState(null);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API_BASE_URL}/public-groups.php`)
      .then(res => { if (vivo && res.data?.registro_config) setRegistroConfig(res.data.registro_config); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const porDni = !!registroConfig?.login_por_dni;
  const sinPassword = !!registroConfig?.login_sin_password;
  const acento = c.color_acento || proyecto?.color_primario || '#21315c';

  const iconoUsuario = useMemo(
    () => renderIcono(porDni ? c.icono_dni : c.icono_email, IconoSobre),
    [porDni, c.icono_dni, c.icono_email]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      const res = await authService.login(
        sinPassword ? { email, passwordless: true } : { email, password }
      );
      const { token, user } = res.data;
      // «Mantenerme conectado» desmarcado: la sesión dura lo que dure el
      // navegador abierto (AuthContext la descarta al volver sin la marca de
      // sesión viva). Marcado: se comporta como siempre.
      if (c.mostrar_recordar !== false && !recordar) {
        localStorage.setItem('auth_solo_sesion', '1');
      } else {
        localStorage.removeItem('auth_solo_sesion');
      }
      sessionStorage.setItem('auth_sesion_viva', '1');
      login({ token, user });
      navigate(c.url_destino || '/contenidos');
    } catch (err) {
      // Cada proyecto tiene su propia cuenta: quien solo está en otro debe registrarse aquí.
      setError(err.response?.data?.code === 'sin_cuenta_proyecto' ? err.response.data.message : t(c.texto_error, 'Credenciales inválidas'));
    } finally {
      setEnviando(false);
    }
  };

  const mostrarEtiquetas = c.mostrar_etiquetas === true;
  // Sin destino propio, el enlace lleva a la recuperación de la plataforma.
  const urlOlvido = c.ocultar_olvido === true ? '' : (c.url_olvido || '/recuperar-contrasena');
  const urlRegistro = c.url_registro || '/register';

  const campoProps = {
    $caja: c.estilo_campos === 'caja',
    $borde: c.color_linea,
    $bordeFocus: c.color_linea_focus,
    $acento: acento,
    $radio: c.radio_campos,
    $margin: c.separacion_campos,
  };

  return (
    <PanelAcceso config={c} tl={tl}>
      <form onSubmit={handleSubmit}>
        <div>
          {mostrarEtiquetas && (
            <Etiqueta $color={c.color_etiquetas} $size={c.tamano_etiquetas}>
              {porDni
                ? t(c.label_dni, 'Número de identificación')
                : t(c.label_email, 'Correo electrónico')}
            </Etiqueta>
          )}
          <Campo {...campoProps}>
            {c.mostrar_iconos !== false && (
              <Icono $color={c.color_iconos} $size={c.tamano_iconos}>{iconoUsuario}</Icono>
            )}
            <Input
              type={porDni ? 'text' : 'email'}
              inputMode={porDni ? 'numeric' : undefined}
              autoComplete={porDni ? 'username' : 'email'}
              placeholder={porDni
                ? t(c.placeholder_dni, 'Número de identificación')
                : t(c.placeholder_email, 'Correo electrónico')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              $size={c.tamano_campos}
              $color={c.color_texto_campos}
              $phColor={c.color_placeholder}
            />
          </Campo>
        </div>

        {!sinPassword && (
          <div>
            {mostrarEtiquetas && (
              <Etiqueta $color={c.color_etiquetas} $size={c.tamano_etiquetas}>
                {t(c.label_password, 'Contraseña')}
              </Etiqueta>
            )}
            <Campo {...campoProps}>
              {c.mostrar_iconos !== false && (
                <Icono $color={c.color_iconos} $size={c.tamano_iconos}>
                  {renderIcono(c.icono_password, IconoLlave)}
                </Icono>
              )}
              <VerContrasena color={c.color_iconos}>
                {tipo => (
                  <Input
                    type={tipo}
                    autoComplete="current-password"
                    placeholder={t(c.placeholder_password, 'Contraseña')}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    $size={c.tamano_campos}
                    $color={c.color_texto_campos}
                    $phColor={c.color_placeholder}
                  />
                )}
              </VerContrasena>
            </Campo>
          </div>
        )}

        {c.mostrar_recordar !== false && (
          <Recordar
            $margin={c.margen_recordar}
            $size={c.tamano_recordar}
            $color={c.color_recordar}
            $box={c.tamano_check}
            $acento={acento}
          >
            <input
              type="checkbox"
              checked={recordar}
              onChange={e => setRecordar(e.target.checked)}
            />
            {t(c.texto_recordar, 'Mantenerme conectado')}
          </Recordar>
        )}

        {error && <Error $color={c.color_error} $bg={c.fondo_error}>{error}</Error>}

        <Acciones $align={c.alineacion_boton} $margin={c.margen_boton}>
          <Boton
            type="submit"
            disabled={enviando}
            $bg={c.boton_fondo}
            $color={c.boton_color}
            $borde={c.boton_borde}
            $radio={c.boton_radio}
            $padding={c.boton_padding}
            $size={c.boton_tamano}
            $weight={c.boton_peso}
            $ancho={c.boton_ancho}
          >
            {enviando ? t(c.texto_boton_enviando, 'Entrando…') : t(c.texto_boton, 'Acceder')}
          </Boton>

          {urlOlvido && (
            <Enlace
              href={urlOlvido}
              $color={c.color_enlace}
              $size={c.tamano_enlace}
              $margin={c.margen_enlace}
            >
              {t(c.texto_olvido, '¿Has olvidado tu contraseña?')}
            </Enlace>
          )}

          {c.mostrar_enlace_registro === true && (
            <Cambio $color={c.color_texto_secundario} $linkColor={c.color_enlace}>
              {t(c.texto_sin_cuenta, '¿No tienes cuenta?')}{' '}
              <a
                href={urlRegistro}
                onClick={(e) => {
                  if (urlRegistro.startsWith('/')) { e.preventDefault(); navigate(urlRegistro); }
                }}
              >{t(c.texto_enlace_registro, 'Regístrate aquí')}</a>
            </Cambio>
          )}
        </Acciones>
      </form>
    </PanelAcceso>
  );
};

export default LoginFormSection;
